import { useSelector } from 'react-redux';
import { DefaultRootState } from '@reduxLocal/persistState';
import { useRoute } from '@react-navigation/native';
import { useMemo } from 'react';
import { Platform } from 'react-native';

/**
 * Hook to check if the current user is in learning mode
 * Learning mode restricts access to paid features
 * 
 * @returns {boolean} True if user is in learning mode
 */
export const useLearningMode = (): boolean => {
  const route = useRoute();
  const userState = useSelector((state: DefaultRootState) => state.promiseData?.data);
  const learningModeState = useSelector((state: DefaultRootState) => 
    state.app?.learningMode || state.promiseData?.learningMode
  );
  
  return useMemo(() => {
    // Check route name for 'Learn'
    const isLearnRoute = route.name === 'Learn';
    
    // Check route params for learning mode flag
    const routeParams = route.params as { learningMode?: boolean } | undefined;
    const hasLearningParam = routeParams?.learningMode === true;
    
    // Check URL params on web
    let hasUrlParam = false;
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search);
      hasUrlParam = urlParams.get('learning') === 'true' || 
                    urlParams.get('learn') === 'true' ||
                    window.location.pathname.includes('/learn');
    }
    
    // Check if user account is marked as learning account
    const isLearningAccount = userState?.userData?.accountType === 'learning' ||
                              userState?.userData?.isLearningAccount === true;
    
    return isLearnRoute || hasLearningParam || hasUrlParam || isLearningAccount || learningModeState === true;
  }, [route.name, route.params, userState, learningModeState]);
};

/**
 * Hook to check if user can access paid features
 * Returns false if user is in learning mode
 * 
 * @returns {boolean} True if user can access paid features
 */
export const useCanAccessPaidFeatures = (): boolean => {
  const isLearningMode = useLearningMode();
  const hasActiveSubscription = useSelector((state: DefaultRootState) => {
    const user = state.promiseData?.data?.userData;
    if (!user) return false;
    
    // Check multiple premium indicators
    const isPremium = user?.isPremium === true;
    const hasActiveSubscription = user?.subscriptionStatus === 'active';
    const hasBillingActive = user?.billing?.isActive === true || user?.billing_info;
    
    return isPremium || hasActiveSubscription || hasBillingActive;
  });
  
  // If in learning mode, always return false (no paid features)
  if (isLearningMode) {
    return false;
  }
  
  return hasActiveSubscription;
};

export default useLearningMode;

